import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { INCREMENT_SYNONYM_SCORE } from './graphql/incrementSynonymScore'

/**
 * Bridges storefront conversions (click / add-to-cart / order) back to the synonym module so it
 * can accumulate behavior_score. Only searches that originated from a reported synonym opportunity
 * are counted, so we don't spam the backend for every search-attributed conversion.
 */

export type SynonymScoreAction = 'PRODUCT_CLICK' | 'ADD_TO_CART' | 'PLACE_ORDER'

const ORIGIN_KEY = 'synonym_request_ids'
const MAX_TRACKED = 50

const readOriginIds = (): string[] => {
    if (typeof window === 'undefined') return []
    try {
        const raw = sessionStorage.getItem(ORIGIN_KEY)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

/**
 * Flag a successful search's request_id as originating from a synonym opportunity.
 */
export const markSynonymOriginRequest = (requestId?: string | null): void => {
    if (typeof window === 'undefined' || !requestId) return
    const ids = readOriginIds().filter((id) => id !== requestId)
    ids.push(requestId)
    try {
        sessionStorage.setItem(
            ORIGIN_KEY,
            JSON.stringify(ids.slice(-MAX_TRACKED))
        )
    } catch {
        // storage full / unavailable — non-critical
    }
}

/**
 * Report a conversion event for the last search, but only when that search came from a synonym.
 */
export const reportSynonymConversion = async (
    action: SynonymScoreAction,
    locale = 'es'
): Promise<void> => {
    if (typeof window === 'undefined') return

    const requestId = sessionStorage.getItem('lastSearchRequestId')
    if (!requestId || !readOriginIds().includes(requestId)) {
        return
    }

    try {
        await graphqlFetch({
            query: INCREMENT_SYNONYM_SCORE,
            variables: { request_id: requestId, action },
            locale: locale || 'es'
        })
    } catch (error) {
        // Silent: conversion tracking must never affect UX.
        console.warn(
            '[LiveSearch][Synonyms] Failed to report conversion:',
            error
        )
    }
}
